import type {
  FrameType,
  IDemo,
  RRWebEvent,
  Tour,
  TourFrame,
  TourStep,
  TourStepConfigUpdate,
} from '@carboncopyco/types';
import type { FrameEventType } from '@carboncopyco/types';
import { Debug } from '@carboncopyco/utils';
import { fetchBaseQuery } from '@reduxjs/toolkit/dist/query';
import { baseQueryCasing } from '../utils/baseQueryCasing';
import { workspaceAddAsset } from '../utils/queryDataUpdateHandler';
import { mainApi } from './mainApi';

Debug.extend('demostack-data:api:tourCreatorApi');

export interface UpdateTourStatusArgs
  extends Pick<Tour, 'id'>,
    Partial<Pick<Tour, 'status'>> {
  organizationId: string;
}

export interface UpdateTourWithNewFramesArgs {
  organizationId: string;
  id: Tour['id'];
  newTourId: Tour['id'];
  insertIndex: number;
}

export interface ReorderTourFramesArgs {
  organizationId: string;
  id: Tour['id'];
  frameIds: TourFrame['id'][];
}

export interface UpdateTourFrameFrameDataUrlArgs {
  organizationId: string;
  tourId: TourFrame['tourData']['id'];
  frameId: TourFrame['id'];
  frameDataUrl: string;
}

export interface UpdateTourFrameTitleArgs {
  organizationId: string;
  tourId: TourFrame['tourData']['id'];
  frameId: TourFrame['id'];
  frameTitle: string;
}

export interface UpdateTourFrameTypeArgs {
  organizationId: string;
  tourId: TourFrame['tourData']['id'];
  frameId: TourFrame['id'];
  frameType: FrameType;
  steps: TourStep[];
}

export interface UpdateTourFrameIsActiveArgs {
  organizationId: string;
  tourId: TourFrame['tourData']['id'];
  frameId: TourFrame['id'];
  isActive: boolean;
}

export interface UpdateTourFrameStepsArgs {
  organizationId: string;
  tourId: TourFrame['tourData']['id'];
  frameId: TourFrame['id'];
  steps: TourStep[];
}

export interface UpdateTourFrameStepArgs {
  organizationId: string;
  tourId: TourFrame['tourData']['id'];
  frameId: TourFrame['id'];
  step: TourStep;
}

export interface UpdateAllTourStepsArgs {
  organizationId: string;
  tourId: TourFrame['tourData']['id'];
  updates: TourStepConfigUpdate[];
}

export interface SaveTourArgs {
  organizationId: string;
  id: Tour['id'];
  comment: string;
}

export interface CreateTourFrameArgs {
  organizationId: string;
  frameActionLabel: string;
  frameDataUrl: string;
  frameIndex: number;
  frameTitle: string;
  pageUrl: string;
  steps?: TourStep[];
  tourId: number;
  pageTitle: string;
  frameEventType: FrameEventType;
  image?: string;
  frameScreenshotUrl?: string;
}

export interface CreateTourScreenshotFrameArgs
  extends Omit<CreateTourFrameArgs, 'frameDataUrl'> {
  frameScreenshotUrl?: string;
  frameType?: FrameType;
  frameDataUrl?: string;
}

export interface GetTourFramesArgs extends Pick<Tour, 'id'> {
  organizationId: string;
  isDeleted?: boolean;
}

export interface DeleteTourFrameArgs extends Pick<TourFrame, 'id'> {
  organizationId: string;
}

export interface RestoreTourFrameArgs extends Pick<TourFrame, 'id'> {
  organizationId: string;
}

export interface GetTourFramesDraftArgs extends Pick<Tour, 'id'> {
  organizationId: string;
}

export interface DiscardFramesDraftsArgs extends Pick<Tour, 'id'> {
  organizationId: string;
}

export interface GetAllTourEventsArgs {
  frameUrls: string[];
  fileServiceToken: string;
}

export interface GetAllTourEventsPerFrameArgs {
  frames: TourFrame[];
  fileServiceToken: string;
}

export interface GetAllTourEventsPerFrameReturn {
  frameId: TourFrame['id'];
  events?: number[];
  screenshotUrl?: string;
}

export interface CreateTourArgs {
  organizationId: string;
  demoId: IDemo['id'];
  description: string;
  tourName: string;
  source?: string;
}

export interface CreateTourFromUrlArgs {
  organizationId: string;
  description: string;
  tourName: string;
  url: string;
  source?: string;
  tourSettings?: Tour['tourSettings'];
}

const tourCreatorApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    saveTour: builder.mutation<Tour, SaveTourArgs>({
      query: ({ organizationId, id, comment }) => ({
        url: `/api/${organizationId}/tour/${id}/save`,
        method: 'POST',
        body: { comment: comment },
      }),
      invalidatesTags: (res) =>
        res && [
          { type: 'WorkspaceAssets', id: res.workspaceId },
          { type: 'Tour', id: res.id },
          { type: 'Revisions', id: res.id },
        ],
    }),

    createTour: builder.mutation<Tour, CreateTourArgs>({
      query: ({ organizationId, demoId, description, tourName, source }) => ({
        url: `/api/${organizationId}/tours/new`,
        method: 'PUT',
        body: {
          demoId,
          description,
          tourName,
          source,
        },
      }),
      invalidatesTags: (res) =>
        res && [{ type: 'WorkspaceAssets', id: res.id }],
    }),
    createTourFromUrl: builder.mutation<Tour, CreateTourFromUrlArgs>({
      query: ({
        description,
        tourName,
        url,
        organizationId,
        source,
        tourSettings,
      }) => ({
        url: `/api/${organizationId}/tours/live/new`,
        method: 'PUT',
        body: {
          description,
          url,
          tourName,
          source,
          tourSettings,
        },
      }),
      invalidatesTags: (res) =>
        res && [{ type: 'WorkspaceAssets', id: res.id }],
    }),
    updateTourStatus: builder.mutation<Tour, UpdateTourStatusArgs>({
      query: ({ organizationId, id, status }) => ({
        url: `/api/${organizationId}/tour/${id}`,
        method: 'POST',
        body: { status },
      }),
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        const state = getState();
        await workspaceAddAsset({
          queryFulfilled,
          state,
          dispatch,
          argumentKey: 'workspaceId',
          entityType: 'tour',
        });
      },
      invalidatesTags: (result) =>
        result
          ? [{ type: 'DemoTours' }, { type: 'Tours' }]
          : [{ type: 'Tours' }],
    }),

    updateTourWithNewFrames: builder.mutation<
      Tour,
      UpdateTourWithNewFramesArgs
    >({
      query: ({ organizationId, id, newTourId, insertIndex }) => {
        return {
          url: `/api/${organizationId}/frames/tour/${id}/draft/add`,
          method: 'POST',
          body: {
            tour_id: newTourId,
            insert_index: insertIndex,
          },
        };
      },
      invalidatesTags: (res) =>
        res
          ? [
              { type: 'TourFrames' },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
            ]
          : [{ type: 'TourFrames' }],
    }),

    reorderTourFrames: builder.mutation<Tour, ReorderTourFramesArgs>({
      query: ({ organizationId, id, frameIds }) => {
        return {
          url: `/api/${organizationId}/frames/tour/${id}/draft/reorder`,
          method: 'POST',
          body: { frame_ids: frameIds },
        };
      },
      invalidatesTags: (res) =>
        res
          ? [
              { type: 'TourFrames' },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
            ]
          : [{ type: 'TourFrames' }],
    }),

    createTourFrame: builder.mutation<TourFrame, CreateTourFrameArgs>({
      query: ({
        organizationId,
        frameActionLabel,
        frameDataUrl,
        frameIndex,
        frameTitle,
        pageUrl,
        steps,
        tourId,
        pageTitle,
        frameEventType,
        image,
        frameScreenshotUrl,
      }) => ({
        url: `/api/${organizationId}/frame/new`,
        method: 'PUT',
        body: {
          frameActionLabel,
          frameDataUrl,
          frameIndex,
          frameTitle,
          pageUrl,
          steps,
          tourId,
          pageTitle,
          frameEventType,
          image,
          frameScreenshotUrl,
        },
      }),
      invalidatesTags: [{ type: 'WorkspaceAssets' }],
    }),

    createTourScreenshotFrame: builder.mutation<
      TourFrame,
      CreateTourScreenshotFrameArgs
    >({
      query: ({
        organizationId,
        frameActionLabel,
        frameIndex,
        frameTitle,
        pageUrl,
        steps,
        tourId,
        pageTitle,
        frameEventType,
        image,
        frameType = 'recording',
        frameScreenshotUrl,
      }) => ({
        url: `/api/${organizationId}/frames/tour/${tourId}/draft/add`,
        method: 'PUT',
        body: {
          frameActionLabel,
          frameIndex,
          frameTitle,
          pageUrl,
          steps,
          tourId,
          pageTitle,
          frameEventType,
          image,
          frameType,
          frameScreenshotUrl,
        },
      }),
      invalidatesTags: [
        { type: 'TourFrames' },
        { type: 'WorkspaceAssets' },
        { type: 'Tour' },
      ],
    }),

    updateTourFrameIsActive: builder.mutation<
      TourFrame,
      UpdateTourFrameIsActiveArgs
    >({
      query: ({ organizationId, frameId, isActive }) => ({
        url: `/api/${organizationId}/frame/${frameId}`,
        method: 'POST',
        body: { isActive },
      }),
      invalidatesTags: [{ type: 'TourFrames' }, { type: 'WorkspaceAssets' }],
      onQueryStarted: async (
        { organizationId, tourId, frameId, isActive },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          tourCreatorApi.util.updateQueryData(
            'getTourFramesDraft',
            { organizationId, id: tourId },
            (frames) => {
              const frameToUpdate = frames.find(
                (frame) => frameId === frame.id
              );
              if (frameToUpdate) {
                frameToUpdate.isActive = isActive;
              }
            }
          )
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),

    updateTourFrameFrameDataUrl: builder.mutation<
      TourFrame,
      UpdateTourFrameFrameDataUrlArgs
    >({
      query: ({ organizationId, frameId, frameDataUrl }) => ({
        url: `/api/${organizationId}/frame/${frameId}`,
        method: 'POST',
        body: { frameDataUrl },
      }),
      invalidatesTags: [{ type: 'TourFrames' }, { type: 'WorkspaceAssets' }],
      onQueryStarted: async (
        { organizationId, tourId, frameId, frameDataUrl },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          tourCreatorApi.util.updateQueryData(
            'getTourFramesDraft',
            { organizationId, id: tourId },
            (frames) => {
              const framesToUpdate = frames.find(
                (frame) => frameId === frame.id
              );
              if (framesToUpdate) {
                framesToUpdate.frameDataUrl = frameDataUrl;
              }
            }
          )
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),

    updateTourFrameTitle: builder.mutation<TourFrame, UpdateTourFrameTitleArgs>(
      {
        query: ({ organizationId, frameId, frameTitle }) => ({
          url: `/api/${organizationId}/frame/${frameId}`,
          method: 'POST',
          body: { frameTitle },
        }),
        invalidatesTags: [{ type: 'TourFrames' }, { type: 'WorkspaceAssets' }],
        onQueryStarted: async (
          { organizationId, tourId, frameId, frameTitle },
          { dispatch, queryFulfilled }
        ) => {
          const patchResult = dispatch(
            tourCreatorApi.util.updateQueryData(
              'getTourFramesDraft',
              { organizationId, id: tourId },
              (frames) => {
                const framesToUpdate = frames.find(
                  (frame) => frameId === frame.id
                );
                if (framesToUpdate) {
                  framesToUpdate.frameTitle = frameTitle;
                }
              }
            )
          );
          queryFulfilled.catch(patchResult.undo);
        },
      }
    ),

    updateTourFrameSteps: builder.mutation<TourFrame, UpdateTourFrameStepsArgs>(
      {
        query: ({ organizationId, frameId, steps }) => ({
          url: `/api/${organizationId}/frame/${frameId}`,
          method: 'POST',
          body: { steps },
        }),
        invalidatesTags: [{ type: 'TourFrames' }, { type: 'WorkspaceAssets' }],
        onQueryStarted: async (
          { organizationId, tourId, frameId, steps },
          { dispatch, queryFulfilled }
        ) => {
          const patchResult = dispatch(
            tourCreatorApi.util.updateQueryData(
              'getTourFramesDraft',
              { organizationId, id: tourId },
              (frames) => {
                const frameToUpdate = frames.find(
                  (frame) => frameId === frame.id
                );
                if (frameToUpdate) {
                  frameToUpdate.steps = steps;
                }
              }
            )
          );
          queryFulfilled.catch(patchResult.undo);
        },
      }
    ),

    updateTourFrameStep: builder.mutation<TourFrame, UpdateTourFrameStepArgs>({
      query: ({ organizationId, frameId, step }) => ({
        url: `/api/${organizationId}/frame/${frameId}/draft/step/${step.stepId}`,
        method: 'POST',
        body: { step },
      }),
      onQueryStarted: async (
        { organizationId, tourId, frameId, step },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          tourCreatorApi.util.updateQueryData(
            'getTourFramesDraft',
            { organizationId, id: tourId },
            (frames) => {
              const frameToUpdate = frames.find(
                (frame) => frameId === frame.id
              );
              if (frameToUpdate) {
                const stepIndexToUpdate = frameToUpdate.steps.findIndex(
                  (stepFromFrame) => step.stepId === stepFromFrame.stepId
                );
                if (stepIndexToUpdate !== -1) {
                  frameToUpdate.steps[stepIndexToUpdate] = step;
                }
              }
            }
          )
        );
        queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: [{ type: 'TourFrames' }, { type: 'WorkspaceAssets' }],
    }),

    updateAllTourSteps: builder.mutation<TourFrame[], UpdateAllTourStepsArgs>({
      query: ({ organizationId, tourId, updates }) => ({
        url: `/api/${organizationId}/frames/tour/${tourId}/draft/step/update-all`,
        method: 'POST',
        body: { updates },
      }),
      invalidatesTags: [{ type: 'TourFrames' }, { type: 'WorkspaceAssets' }],
    }),

    deleteTourFrame: builder.mutation<TourFrame, DeleteTourFrameArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/frame/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'TourFrames' },
        { type: 'WorkspaceAssets' },
        { type: 'Tour' },
      ],
    }),

    restoreTourFrame: builder.mutation<TourFrame, RestoreTourFrameArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/frame/${id}/restore`,
        method: 'POST',
      }),
      invalidatesTags: [
        { type: 'TourFrames' },
        { type: 'WorkspaceAssets' },
        { type: 'Tour' },
      ],
    }),

    getTourFrames: builder.query<TourFrame[], GetTourFramesArgs>({
      query: ({ organizationId, id, isDeleted = false }) => ({
        url: `/api/${organizationId}/frames/tour/${id}`,
        method: 'GET',
        params: isDeleted ? { removeDeleted: 'false' } : null,
      }),
      providesTags: () => ['TourFrames'],
    }),

    getAllTourEvents: builder.query<RRWebEvent[][], GetAllTourEventsArgs>({
      providesTags: () => ['TourEvents'],
      queryFn: async (arg, queryApi, extraOptions) => {
        try {
          const fileLinksResponse = await Promise.all(
            arg.frameUrls.map((url) =>
              baseQueryCasing(fetchBaseQuery({ credentials: 'include' }))(
                {
                  url: `${url}?token=${arg.fileServiceToken}`,
                  method: 'GET',
                },
                queryApi,
                extraOptions
              )
            )
          );

          const fileLinks = fileLinksResponse.map(
            (res) => (res.data as { signedUrl: string }).signedUrl
          );

          const tourEventsResponse = await Promise.all(
            fileLinks.map((link) => fetch(`${link}`))
          );

          const tourEventsTexts = await Promise.all(
            tourEventsResponse.map((response) => response.text())
          );

          const tourEventsPerFrame = tourEventsTexts.map((eventText) =>
            JSON.parse(eventText)
          );

          return { data: tourEventsPerFrame };
        } catch (_error) {
          return {
            error: {
              status: 400,
              statusText: 'FETCH_ERROR',
              data: 'GOT AN EXCEPTION',
            },
          };
        }
      },
    }),

    getAllTourEventsPerFrame: builder.query<
      GetAllTourEventsPerFrameReturn[],
      GetAllTourEventsPerFrameArgs
    >({
      providesTags: () => ['TourEvents'],
      queryFn: async (arg, queryApi, extraOptions) => {
        try {
          const framesResponses = await Promise.all(
            arg.frames.map(async (frame) => {
              if (
                frame.frameType === 'screenshot' &&
                !!frame.frameScreenshotUrl
              ) {
                return {
                  frameId: frame.id,
                  screenshotUrl: frame.frameScreenshotUrl,
                };
              }

              const fileLinkResponse = await baseQueryCasing(
                fetchBaseQuery({ credentials: 'include' })
              )(
                {
                  url: `${frame.frameDataUrl}?token=${arg.fileServiceToken}`,
                  method: 'GET',
                },
                queryApi,
                extraOptions
              );
              const fileLink = (
                fileLinkResponse.data as {
                  signedUrl: string;
                }
              ).signedUrl;
              const tourEventsResponse = await fetch(`${fileLink}`);
              const tourEventsText = await tourEventsResponse.text();
              const tourEventsPerFrame = JSON.parse(tourEventsText);
              return {
                frameId: frame.id,
                events: tourEventsPerFrame,
              };
            })
          );
          return { data: framesResponses };
        } catch (error) {
          return {
            error: {
              status: error.status,
              statusText: error.statusText,
              data: error.data,
            },
          };
        }
      },
    }),

    getTourFramesDraft: builder.query<TourFrame[], GetTourFramesDraftArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/frames/tour/${id}/draft`,
        method: 'GET',
      }),
      providesTags: () => ['TourFrames'],
    }),

    discardFramesDrafts: builder.mutation<TourFrame[], DiscardFramesDraftsArgs>(
      {
        query: ({ organizationId, id }) => ({
          url: `/api/${organizationId}/frames/tour/${id}/draft/discard`,
          method: 'POST',
          /*
            Since this response always returns null with this request the regular responseHandler RTK Query has
            set up fails to JSON.parse() and throws an error, that's why we had to add this responseHandler key
          */
          responseHandler: (response: { text: () => any }) => response.text(),
        }),
        invalidatesTags: [
          { type: 'TourFrames' },
          { type: 'WorkspaceAssets' },
          { type: 'Tour' },
        ],
      }
    ),

    updateTourFrameFrameType: builder.mutation<
      TourFrame,
      UpdateTourFrameTypeArgs
    >({
      query: ({ organizationId, frameId, frameType, steps }) => ({
        url: `/api/${organizationId}/frame/${frameId}`,
        method: 'POST',
        body: { frameType, steps },
      }),
      invalidatesTags: [{ type: 'TourFrames' }, { type: 'Tour' }],
    }),
  }),
});

export const {
  useGetAllTourEventsQuery,
  useGetAllTourEventsPerFrameQuery,
  useGetTourFramesQuery,
  useCreateTourMutation,
  useCreateTourFromUrlMutation,
  useCreateTourFrameMutation,
  useCreateTourScreenshotFrameMutation,
  useUpdateTourStatusMutation,
  useGetTourFramesDraftQuery,
  useSaveTourMutation,
  useUpdateTourFrameStepsMutation,
  useUpdateTourFrameStepMutation,
  useUpdateAllTourStepsMutation,
  useDiscardFramesDraftsMutation,
  useUpdateTourFrameFrameDataUrlMutation,
  useUpdateTourFrameIsActiveMutation,
  useUpdateTourFrameTitleMutation,
  useDeleteTourFrameMutation,
  useRestoreTourFrameMutation,
  useUpdateTourWithNewFramesMutation,
  useReorderTourFramesMutation,
  useUpdateTourFrameFrameTypeMutation,
} = tourCreatorApi;

// we didn't want to use RTK query for fetching tour events
// since it creates performance bottleneck on tours with large number of events
export const fetchAllTourEvents = async (
  frameUrls: string[],
  fileServiceToken: string
) => {
  console.time('[perf]: fetching tourEvent URLs from file service');
  const fileLinksResponse = await Promise.all(
    frameUrls.map((url) =>
      fetch(`${url}?token=${fileServiceToken}`).then((res) => res.json())
    )
  );
  console.timeEnd('[perf]: fetching tourEvent URLs from file service');

  const fileLinks = fileLinksResponse.map(
    (res) => (res as { signed_url: string }).signed_url
  );

  console.time('[perf]: fetching tourEvents from CDN');
  const tourEventsTexts = await Promise.all(
    fileLinks.map((link) => fetch(`${link}`).then((res) => res.text()))
  );
  console.timeEnd('[perf]: fetching tourEvents from CDN');

  const tourEventsPerFrame = tourEventsTexts.map((eventText) =>
    JSON.parse(eventText)
  );

  return tourEventsPerFrame as RRWebEvent[][];
};
